<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    PageHeader,
    NextButton,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      channelName: '',
      oaId: '',
      appId: '',
      appSecret: '',
      oaSecretKey: '',
      accessToken: '',
      refreshToken: '',
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
  },
  validations: {
    channelName: { required },
    oaId: { required },
    appId: { required },
    appSecret: { required },
    oaSecretKey: { required },
    accessToken: { required },
    refreshToken: { required },
  },
  methods: {
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      try {
        const zaloChannel = await this.$store.dispatch(
          'inboxes/createChannel',
          {
            name: this.channelName?.trim(),
            channel: {
              type: 'zalo',
              oa_id: this.oaId,
              app_id: this.appId,
              app_secret: this.appSecret,
              oa_secret_key: this.oaSecretKey,
              access_token: this.accessToken,
              refresh_token: this.refreshToken,
            },
          }
        );

        router.replace({
          name: 'settings_inboxes_add_agents',
          params: {
            page: 'new',
            inbox_id: zaloChannel.id,
          },
        });
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.ADD.ZALO_CHANNEL.API.ERROR_MESSAGE'));
      }
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.ZALO_CHANNEL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.ZALO_CHANNEL.DESC')"
    />
    <form
      class="flex flex-wrap flex-col mx-0"
      @submit.prevent="createChannel()"
    >
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.channelName.$error }">
          {{ $t('INBOX_MGMT.ADD.ZALO_CHANNEL.CHANNEL_NAME.LABEL') }}
          <input
            v-model="channelName"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.ZALO_CHANNEL.CHANNEL_NAME.PLACEHOLDER')
            "
            @blur="v$.channelName.$touch"
          />
          <span v-if="v$.channelName.$error" class="message">{{
            $t('INBOX_MGMT.ADD.ZALO_CHANNEL.CHANNEL_NAME.ERROR')
          }}</span>
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.oaId.$error }">
          {{ $t('INBOX_MGMT.ADD.ZALO_CHANNEL.OA_ID.LABEL') }}
          <input
            v-model="oaId"
            type="text"
            :placeholder="$t('INBOX_MGMT.ADD.ZALO_CHANNEL.OA_ID.PLACEHOLDER')"
            @blur="v$.oaId.$touch"
          />
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.appId.$error }">
          {{ $t('INBOX_MGMT.ADD.ZALO_CHANNEL.APP_ID.LABEL') }}
          <input
            v-model="appId"
            type="text"
            :placeholder="$t('INBOX_MGMT.ADD.ZALO_CHANNEL.APP_ID.PLACEHOLDER')"
            @blur="v$.appId.$touch"
          />
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.appSecret.$error }">
          {{ $t('INBOX_MGMT.ADD.ZALO_CHANNEL.APP_SECRET.LABEL') }}
          <input
            v-model="appSecret"
            type="password"
            :placeholder="
              $t('INBOX_MGMT.ADD.ZALO_CHANNEL.APP_SECRET.PLACEHOLDER')
            "
            @blur="v$.appSecret.$touch"
          />
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.oaSecretKey.$error }">
          {{ $t('INBOX_MGMT.ADD.ZALO_CHANNEL.OA_SECRET_KEY.LABEL') }}
          <input
            v-model="oaSecretKey"
            type="password"
            :placeholder="
              $t('INBOX_MGMT.ADD.ZALO_CHANNEL.OA_SECRET_KEY.PLACEHOLDER')
            "
            @blur="v$.oaSecretKey.$touch"
          />
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.accessToken.$error }">
          {{ $t('INBOX_MGMT.ADD.ZALO_CHANNEL.ACCESS_TOKEN.LABEL') }}
          <input
            v-model="accessToken"
            type="password"
            :placeholder="
              $t('INBOX_MGMT.ADD.ZALO_CHANNEL.ACCESS_TOKEN.PLACEHOLDER')
            "
            @blur="v$.accessToken.$touch"
          />
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.refreshToken.$error }">
          {{ $t('INBOX_MGMT.ADD.ZALO_CHANNEL.REFRESH_TOKEN.LABEL') }}
          <input
            v-model="refreshToken"
            type="password"
            :placeholder="
              $t('INBOX_MGMT.ADD.ZALO_CHANNEL.REFRESH_TOKEN.PLACEHOLDER')
            "
            @blur="v$.refreshToken.$touch"
          />
        </label>
      </div>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="uiFlags.isCreating"
          type="submit"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.ZALO_CHANNEL.SUBMIT_BUTTON')"
        />
      </div>
    </form>
  </div>
</template>
